import React, { useEffect, useContext, useState } from "react";
import { observer } from "mobx-react";
import { useParams, useHistory } from "react-router-dom";
import makeApi, { URLS } from "Utils/apiURL";
import Utils from "Utils/utils";
import globalCallState from "Stores/GlobalCallState";
import DialerServiceData from "Stores/DialerServiceStore";
import { AccountsContext } from "../Main";
import { accountsState } from "../AccountsState";
import { toasterState } from "Components/common/toaster";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { upgradePopupState } from "Components/Paywall/UpgradePopup";
// UpdateContactStageDropdown replaced with custom AccountStageChip below
import { employeeCountDropdown, revenueRange } from "../AccountConstants";
import { updateContactOwnerState } from "Components/common/UpdateContactOwner";
import { ReactComponent as LinkedinIcon } from "Assets/svg/linkedin.svg";
import AccountIntelligenceSection from "./AccountIntelligenceSection";

const E = "\u2014";

const val  = v   => (v != null && String(v).trim() !== "" ? v : null);
const list = arr => (Array.isArray(arr) && arr.length ? arr : null);
const num  = v   => (v != null ? Number(v) : 0);
const stripMd = s => s ? s.replace(/\*\*/g, "").replace(/\*/g, "").replace(/^#+\s*/gm, "").replace(/^-\s*/gm, "\u2022 ") : "";

const fmtDate = d => {
  if (!d) return null;
  try {
    return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch (e) { return null; }
};

const fmtPhone = phoneSet => {
  if (!phoneSet) return null;
  const arr = Array.isArray(phoneSet) ? phoneSet : [...phoneSet];
  return arr.length > 0 ? arr[0] : null;
};

const SENIORITY_LEVELS = [
  { label: "C-Suite",  key: "C-Suite",  filter: "c_suite",  icon: "\uD83D\uDC51" },
  { label: "VP",       key: "VP",       filter: "vp",        icon: "\u2B50" },
  { label: "Director", key: "Director", filter: "director",  icon: "\uD83C\uDFAF" },
  { label: "Manager",  key: "Manager",  filter: "manager",   icon: "\uD83D\uDCCB" },
  { label: "Staff",    key: "Staff",    filter: "staff",     icon: "\uD83D\uDCBC" },
];

const techIconUrl = name =>
  `https://logo.clearbit.com/${name.toLowerCase().replace(/\s+/g, "")}.com`;

const Main = observer(() => {
  const { accountId = "" } = useParams();
  const history = useHistory();
  const { updateBreadCumb = () => {} } = useContext(AccountsContext);

  const [account,      setAccount]      = useState(null);
  const [subsidiaries, setSubsidiaries] = useState([]);
  const [selectedSub,  setSelectedSub]  = useState(null);
  const [subCache,     setSubCache]     = useState({});
  const [premiumEnriching, setPremiumEnriching] = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [loadingSlow,  setLoadingSlow]  = useState(false);
  const [insights,     setInsights]     = useState(null);
  const [overall,      setOverall]      = useState(null);
  const [emailData,    setEmailData]    = useState(null);
  const [aiAnalysis,   setAiAnalysis]   = useState(null);
  const [aiDate,       setAiDate]       = useState(null);
  const [aiSaved,      setAiSaved]      = useState(false);
  const [aiLoading,    setAiLoading]    = useState(false);
  const [stage,        setStage]        = useState("");

  useEffect(() => { updateBreadCumb("overview"); }, []);

  useEffect(() => {
    loadOverview(accountId);
    loadSubsidiaries(accountId);
    loadProspectInsights(accountId);
    loadAccountInsights(accountId);
    loadEmailInsights(accountId);
    loadPreviousAiAnalysis(accountId);
  }, [accountId]);

  const loadOverview = async id => {
    setLoading(true);
    setLoadingSlow(false);
    const slowTimer = setTimeout(() => setLoadingSlow(true), 2000);
    try {
      const r = await makeApi({ url: `${URLS.getAccountOverview}/${id}/overview`, method: "GET" });
      setAccount(r?.status === 200 ? r.data : null);
    } catch (e) { console.error(e); }
    finally {
      clearTimeout(slowTimer);
      setLoadingSlow(false);
      setLoading(false);
    }
  };

  const loadSubsidiaries = async id => {
    try {
      const r = await makeApi({ url: `${URLS.getAccountSubsidiaries}/${id}/subsidiaries`, method: "GET" });
      if (Array.isArray(r?.data) && r.data.length) setSubsidiaries(r.data);
    } catch (e) {}
  };

  const loadProspectInsights = async id => {
    try {
      const r = await makeApi({ url: `${URLS.getProspectInsights}/${id}/prospect-insights`, method: "GET" });
      if (r?.status === 200 && r.data) setInsights(r.data);
    } catch (e) { console.error(e); }
  };

  const loadAccountInsights = async id => {
    try {
      const r = await makeApi({ url: `${URLS.getAccountInsights}/${id}`, method: "GET" });
      if (r?.status === 200 && r.data) setOverall(r.data?.accountOverallInsights || null);
    } catch (e) { console.error(e); }
  };

  const loadEmailInsights = async id => {
    try {
      const r = await makeApi({ url: `${URLS.getAccountEmailInsights}/${id}`, method: "GET" });
      if (r?.status === 200 && r.data) {
        setEmailData(r.data);
        setStage(r.data?.account?.stage || "");
      }
    } catch (e) { console.error(e); }
  };

  const loadPreviousAiAnalysis = async id => {
    try {
      const r = await makeApi({
        url: URLS.getAccountNotes,
        method: "POST",
        data: { type: "ACCOUNT", referenceId: id, pageNo: 1, limit: 20, currentPage: 1 }
      });
      const notes = r?.data?.result?.notes || r?.data?.notes || [];
      const aiNote = notes.find(n => n.title && n.title.startsWith("AI Prospect Analysis"));
      if (aiNote) {
        setAiAnalysis(aiNote.text);
        setAiDate(aiNote.updateTs ? fmtDate(aiNote.updateTs) : null);
        setAiSaved(true);
      }
    } catch (e) {}
  };

  const handleSubSelect = async id => {
    setSelectedSub(id || null);
    if (id && !subCache[id]) {
      const r = await makeApi({ url: `${URLS.getAccountOverview}/${id}/overview`, method: "GET" });
      if (r?.status === 200) setSubCache(p => ({ ...p, [id]: r.data }));
    }
    const targetId = id || accountId;
    loadProspectInsights(targetId);
    loadAccountInsights(targetId);
    loadEmailInsights(targetId);
    setAiAnalysis(null);
    setAiDate(null);
    setAiSaved(false);
    loadPreviousAiAnalysis(targetId);
  };

  const handlePremiumEnrichConfirm = () => {
    confirmationPopupState.setPopupValues({
      content: `<div style="text-align:left;">
        <p style="color:#343A40;font-size:15px;font-weight:600;margin-bottom:12px;">Advanced Account Enrichment</p>
        <p style="color:#8D8D8D;font-size:13px;margin-bottom:12px;">This will use <b style="color:#343A40;">1 credit</b> to enrich the following data:</p>
        <ul style="color:#343A40;font-size:13px;line-height:2;padding-left:20px;">
          <li>Technologies & Tech Stack</li>
          <li>Funding Data & Investors</li>
        </ul>
      </div>`,
      actionBtnText: "Enrich (1 Credit)",
      cancelBtnText: "Cancel",
      cancelCbk: () => confirmationPopupState.setShowPopup(false),
      callback: () => {
        confirmationPopupState.setShowPopup(false);
        handlePremiumEnrich();
      }
    });
    confirmationPopupState.setShowPopup(true);
  };

  const handlePremiumEnrich = async () => {
    const id = selectedSub || accountId;
    setPremiumEnriching(true);
    try {
      const r = await makeApi({
        url: `${URLS.enrichAccount}/${id}/enrich`,
        method: "POST",
        data: { type: "PREMIUM" }
      });
      if (r?.data?.code === 207) {
        confirmationPopupState.setPopupValues({
          content: `<div style="text-align:left;">
            <p style="color:#343A40;font-size:15px;font-weight:600;margin-bottom:8px;">Insufficient Credits</p>
            <p style="color:#8D8D8D;font-size:13px;">You don't have enough credits for advanced enrichment. Upgrade your plan to get more credits.</p>
          </div>`,
          actionBtnText: "Upgrade",
          cancelBtnText: "Cancel",
          cancelCbk: () => confirmationPopupState.setShowPopup(false),
          callback: () => {
            confirmationPopupState.setShowPopup(false);
            history.push("/settings/billing");
          }
        });
        confirmationPopupState.setShowPopup(true);
      } else if (r?.data?.status === "success") {
        const credits = r?.data?.creditsUsed || 0;
        toasterState.setToastMsg(`Advanced enrichment completed${credits > 0 ? ` (${credits} credit used)` : ""}`, "success");
        if (selectedSub) {
          const d = await makeApi({ url: `${URLS.getAccountOverview}/${id}/overview`, method: "GET" });
          if (d?.status === 200) setSubCache(p => ({ ...p, [id]: d.data }));
        } else {
          loadOverview(id);
        }
      } else {
        toasterState.setToastMsg("Enrichment failed", "fail");
      }
    } catch (e) {
      toasterState.setToastMsg("Enrichment failed", "fail");
    }
    finally { setPremiumEnriching(false); }
  };

  const handleAiAnalysis = async (forceNew = false) => {
    if (aiLoading) return;
    if (aiAnalysis && !forceNew) return;
    const id = selectedSub || accountId;
    setAiLoading(true);
    setAiSaved(false);
    try {
      const r = await makeApi({
        url: `${URLS.aiProspectAnalysis}/${id}/ai-prospect-analysis`,
        method: "POST",
        data: {}
      });
      if (r?.status === 200 && r.data?.analysis) {
        const analysis = r.data.analysis;
        const today = new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
        setAiAnalysis(analysis);
        setAiDate(today);
        // Auto-save to account notes
        makeApi({
          url: URLS.addAccountNotes,
          method: "POST",
          data: {
            referenceId: id,
            type: "ACCOUNT",
            title: `AI Prospect Analysis \u2014 ${today}`,
            text: stripMd(analysis)
          }
        }).then(() => setAiSaved(true)).catch(() => {});
      }
    } catch (e) { console.error(e); }
    finally { setAiLoading(false); }
  };

  // Inline edit save — builds payload matching EditAccountInfoPopup format
  const saveField = async (fieldKey, newValue) => {
    const id = selectedSub || accountId;
    const payload = { id };

    if (fieldKey === "phone") {
      payload.phone = newValue ? [newValue] : [];
    } else if (fieldKey === "industry") {
      payload.industry = newValue || null;
    } else if (fieldKey === "employeeCount") {
      payload.employeeCount = newValue || null;
    } else if (fieldKey === "revenue") {
      payload.revenue = newValue || null;
    } else if (fieldKey === "linkedinId") {
      payload.linkedinId = Utils.removeHttpFromString(newValue || null);
    } else if (fieldKey === "twitter") {
      payload.twitter = Utils.removeHttpFromString(newValue || null);
    } else if (fieldKey === "city" || fieldKey === "state" || fieldKey === "country") {
      const addr = Array.isArray(acctMeta.address) ? acctMeta.address[0] : {};
      payload.address = [{
        id: 1,
        city: fieldKey === "city" ? newValue : (addr?.city || null),
        state: fieldKey === "state" ? newValue : (addr?.state || null),
        country: fieldKey === "country" ? newValue : (addr?.country || null),
      }];
    } else {
      payload[fieldKey] = newValue || null;
    }

    try {
      const r = await makeApi({ url: URLS.updateAccount, method: "POST", data: payload });
      if (+r?.data?.code === 200) {
        toasterState.setToastMsg("Account updated", "success");
        loadOverview(id);
        loadEmailInsights(id);
        return true;
      } else if (r?.data?.code === 403) {
        toasterState.setToastMsg("You don't have editing access", "failure");
      } else {
        toasterState.setToastMsg("Failed to update account", "failure");
      }
    } catch (e) {
      toasterState.setToastMsg("Failed to update account", "failure");
    }
    return false;
  };

  const handleChangeOwner = () => {
    const id = selectedSub || accountId;
    const ownerUpdateCb = async (ownerId) => {
      try {
        const data = { accountIds: [id], ownerId };
        const r = await makeApi({ url: URLS.updateAccountOwner, method: "POST", data });
        // setBtnLoader(false) triggers the popup's auto-close useEffect
        updateContactOwnerState.setBtnLoader(false);
        if (r?.data?.successCount > 0) {
          toasterState.setToastMsg("Account owner updated successfully", "success");
          loadEmailInsights(id);
        } else if (r?.response?.status === 401) {
          toasterState.setToastMsg("You don't have access to update the account owner", "fail");
        } else {
          toasterState.setToastMsg("Something went wrong. Please try again later", "fail");
        }
      } catch (e) {
        updateContactOwnerState.setBtnLoader(false);
        toasterState.setToastMsg("Something went wrong. Please try again later", "fail");
      }
    };
    updateContactOwnerState.setPopupType("ACCOUNT");
    updateContactOwnerState.setCurrentOwnerEmail(owner?.email || "");
    updateContactOwnerState.setTotalCount(1);
    updateContactOwnerState.setCallback(ownerUpdateCb);
    updateContactOwnerState.setShowPopup(true);
  };

  const handleCall = async number => {
    if (!number) return;
    await DialerServiceData.getDailerToken();
    DialerServiceData.setDialerCallLogs(false);
    globalCallState.setPopupValues({
      ...globalCallState.popupValues,
      pageType: "accounts",
      selectedNumber: number,
      isFromContactSection: false,
      isContactCard: false,
    });
    globalCallState.setShowCallSlider(true);
  };

  const goToListBuilder = (filterKey, filterValue) => {
    const domain = a?.domain || "";
    if (!domain) return;
    const filters = {
      companyDomain: [domain],
      companySearchType: "domain",
    };
    if (filterKey && filterValue) filters[filterKey] = [filterValue];
    const payload = JSON.stringify({ filters });
    const sgLb = btoa(unescape(encodeURIComponent(payload)))
      .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
    history.push(`/list-builder?sgLb=${sgLb}`);
  };

  const reloadOverview = () => {
    const id = selectedSub || accountId;
    loadEmailInsights(id);
    loadAccountInsights(id);
  };

  const a = selectedSub ? (subCache[selectedSub] || account) : account;
  const acctMeta = emailData?.account || {};
  const owner = acctMeta.owner || null;

  const locationParts = (() => {
    const addr = Array.isArray(a?.address) ? a.address[0] : null;
    return { city: addr?.city || "", state: addr?.state || "", country: addr?.country || "" };
  })();
  const locationStr = [locationParts.city, locationParts.state, locationParts.country].filter(Boolean).join(", ") || val(a?.locality);

  const employees =
    a?.liEmployeeTotal != null
      ? `${Number(a.liEmployeeTotal).toLocaleString()}${a.employeeCount ? ` (${a.employeeCount})` : ""}`
      : val(a?.employeeCount);

  const unenriched = !val(a?.liDescription) && !val(a?.revenue) && !val(employees);
  const premiumEnriched = !!a?.premiumEnriched;
  const totalContacts = insights?.totalContacts || 0;
  const bySeniority = insights?.bySeniority || {};
  const mostContacted = insights?.mostContacted || null;
  const companyName = val(a?.name) || val(a?.domain) || "this company";

  const emailIns = emailData?.accountEmailInsights || {};
  const sentCount    = num(emailIns.sentCount);
  const openCount    = num(emailIns.openCount);
  const clickCount   = num(emailIns.clickCount);
  const replyCount   = num(emailIns.replyCount);
  const liCrSent     = num(emailIns.liCrSentCount);
  const liCrAccepted = num(emailIns.liCrAcceptedCount);

  const callsMade       = num(overall?.callsMade);
  const tasksCompleted  = num(overall?.tasksCompleted);
  const tasksInDue      = num(overall?.tasksInDue);

  const acctPhone      = fmtPhone(acctMeta.phone);
  const createdDate    = fmtDate(acctMeta.createTs);
  const firstContacted = fmtDate(acctMeta.firstContacted);
  const lastContacted  = fmtDate(acctMeta.lastContacted);

  if (loading) return (
    <>
      {loadingSlow && (
        <div className="acov-enrich-progress">
          <span className="acov-enrich-spinner" />
          <span>Enriching account data, this may take a moment...</span>
        </div>
      )}
      <Skeleton />
    </>
  );

  const wasAutoEnriched = a?.autoEnriched;

  return (
    <div className="acov">

      {wasAutoEnriched && (
        <div className="acov-enrich-banner">
          <span className="acov-enrich-banner__icon">{"\u2726"}</span>
          Account data was just enriched automatically
        </div>
      )}

      {/* -- Header --------------------------------------------------- */}
      <div className="acov-header">
        <div className="acov-header__left">
          {a?.logoUrl && (
            <img src={a.logoUrl} alt="" className="acov-header__logo"
              onError={e => { e.target.style.display = "none"; }} />
          )}
          <div className="acov-header__title">
            <span className="acov-header__name">{val(a?.name) || val(a?.domain) || "Unnamed account"}</span>
            {val(a?.domain) && <span className="acov-header__domain">{a.domain}</span>}
          </div>
          <div className="acov-header__stage">
            <span className="acov-header__stage-label">Stage</span>
            <AccountStageChip
              stage={stage}
              setStage={setStage}
              stages={accountsState?.accountStages || []}
              accountId={selectedSub || accountId}
              reloadCbk={reloadOverview}
            />
          </div>
          <div className="acov-header__owner">
            <span className="acov-header__owner-label">Owner</span>
            <div className="acov-header__owner-info acov-header__owner-info--clickable"
              onClick={handleChangeOwner}
              title={owner ? `${Utils.getOwnerName(owner)} — click to change` : "Assign owner"}>
              {owner ? (
                <>
                  <span className="acov-header__owner-avatar">
                    {Utils.getOwnerInitial(owner)}
                  </span>
                  <span className="acov-header__owner-name">{Utils.getOwnerName(owner)}</span>
                </>
              ) : (
                <span className="acov-header__owner-name acov-header__owner-name--empty">Assign</span>
              )}
              <i className="material-icons acov-header__owner-edit">edit</i>
            </div>
          </div>
          <div className="acov-header__dates">
            <div className="acov-header__date">
              <span className="acov-header__date-label">Created</span>
              <span className="acov-header__date-value">{createdDate || E}</span>
            </div>
            <div className="acov-header__date">
              <span className="acov-header__date-label">First Contacted</span>
              <span className="acov-header__date-value">{firstContacted || E}</span>
            </div>
            <div className="acov-header__date">
              <span className="acov-header__date-label">Last Contacted</span>
              <span className="acov-header__date-value">{lastContacted || E}</span>
            </div>
          </div>
        </div>
        <div className="acov-header__right">
          {subsidiaries.length > 0 && (
            <select className="acov-sub-select" value={selectedSub || ""}
              onChange={e => handleSubSelect(e.target.value || null)}>
              <option value="">{account?.name || account?.domain || "Parent"}</option>
              {subsidiaries.map(s => (
                <option key={s.id} value={s.id}>{s.name || s.domain}</option>
              ))}
            </select>
          )}
          {!premiumEnriched ? (
            <button className="acov-enrich-btn acov-enrich-btn--premium"
              onClick={handlePremiumEnrichConfirm} disabled={premiumEnriching}>
              {premiumEnriching
                ? <><span className="acov-spinner acov-spinner--premium" />Enriching</>
                : <><span className="acov-enrich-btn__icon">{"\u2726"}</span>Advanced Enrich<span className="acov-enrich-btn__tag acov-enrich-btn__tag--credit">1 Credit</span></>}
            </button>
          ) : null}
        </div>
      </div>

      {/* -- Two-column body ----------------------------------------- */}
      <div className="acov-body">

        {/* -- LEFT: Company intel ----------------------------------- */}
        <div className="acov-col acov-col--left">

          <section className="acov-section">
            <h4 className="acov-section__title"><span className="acov-star acov-star--blue">{"\u2726"}</span>About</h4>
            {val(a?.liDescription)
              ? <p className="acov-about__desc">{a.liDescription}</p>
              : <p className="acov-about__empty">No description available.</p>
            }
            {(val(a?.founded) || val(a?.companyType)) && (
              <div className="acov-about__meta">
                {val(a?.founded) && <span>Founded {a.founded}</span>}
                {val(a?.companyType) && <span>{a.companyType.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}</span>}
              </div>
            )}
          </section>

          <section className="acov-section">
            <h4 className="acov-section__title"><span className="acov-star acov-star--violet">{"\u2726"}</span>Company details</h4>
            <div className="acov-details">
              <EditableRow label="Revenue" value={val(a?.revenue)} fieldKey="revenue"
                onSave={saveField} type="select" options={revenueRange} />
              <EditableRow label="Employees" value={employees} fieldKey="employeeCount"
                onSave={saveField} type="select" options={employeeCountDropdown} />
              <EditableRow label="Industry" value={val(a?.industry)} fieldKey="industry" onSave={saveField} />
              <EditableRow label="Location" value={locationStr} fieldKey="city" onSave={saveField}
                multiField={{ city: locationParts.city, state: locationParts.state, country: locationParts.country }} />
              <PhoneRow value={acctPhone} onCall={handleCall} fieldKey="phone" onSave={saveField} />
              <Row label="Founded" value={val(a?.founded)} />
              <Row label="Type" value={val(a?.companyType)?.replace(/_/g, " ")?.replace(/\b\w/g, c => c.toUpperCase())} />
              {val(a?.website) && <Row label="Website" value={a.website} link />}
              <EditableRow label="LinkedIn" value={val(a?.linkedInUrl)} fieldKey="linkedinId" onSave={saveField} />
              <EditableRow label="Twitter" value={val(a?.twitter)} fieldKey="twitter" onSave={saveField} />
              {val(a?.facebook) && <Row label="Facebook" value={a.facebook} link />}
              {val(a?.companyPhone) && <Row label="Company Phone" value={a.companyPhone} />}
              {val(a?.companyEmail) && <Row label="Company Email" value={a.companyEmail} link />}
              {val(a?.legalName) && <Row label="Legal Name" value={a.legalName} />}
              {list(a?.specialities) &&
                <Row label="Specialities" value={a.specialities.join(", ")} />}
            </div>
          </section>

          <section className="acov-section acov-section--premium">
            <h4 className="acov-section__title">
              <span className="acov-stars" style={{color: "#7c3aed"}}>{"\u2726\u2726\u2726"}</span>Technologies
            </h4>
            {list(a?.techStack)
              ? <div className="acov-tech-grid">
                  {(list(a?.techStackDetailed) || a.techStack.map(t => ({ name: t }))).slice(0, 24).map((t, i) => (
                    <div key={i} className="acov-tech-item">
                      <img src={techIconUrl(typeof t === "string" ? t : t.name)} alt={typeof t === "string" ? t : t.name} className="acov-tech-icon"
                        onError={e => { e.target.style.display = "none"; }} />
                      <span className="acov-tech-name">{typeof t === "string" ? t : t.name}</span>
                      {t.category && <span className="acov-tech-category">{t.category}</span>}
                    </div>
                  ))}
                </div>
              : <div className="acov-locked">
                  <div className="acov-locked__preview">
                    {["Salesforce", "HubSpot", "Slack", "AWS", "Jira", "GitHub", "Google Analytics", "Zendesk", "Stripe", "Intercom", "Segment", "Snowflake"].map((t, i) => (
                      <div key={i} className="acov-tech-item acov-tech-item--blur">
                        <img src={techIconUrl(t)} alt={t} className="acov-tech-icon"
                          onError={e => { e.target.style.display = "none"; }} />
                        <span className="acov-tech-name">{t}</span>
                      </div>
                    ))}
                  </div>
                  <div className="acov-locked__cta">
                    <span className="acov-locked__icon">{"\uD83D\uDD12"}</span>
                    <span>Unlock tech stack data</span>
                    <button className="acov-locked__btn acov-locked__btn--premium" onClick={handlePremiumEnrichConfirm} disabled={premiumEnriching}>
                      {premiumEnriching ? "Enriching\u2026" : <><span>{"\u2726"}</span> Advanced Enrich <span className="acov-locked__credit">1 Credit</span></>}
                    </button>
                  </div>
                </div>
            }
          </section>


          {/* Funding — locked state if not premium enriched */}
          {!premiumEnriched && !(a?.lastFundingType || a?.fundingRounds > 0 || a?.ipoDate) && (
            <section className="acov-section acov-section--premium">
              <h4 className="acov-section__title">
                <span className="acov-stars" style={{color: "#7c3aed"}}>{"\u2726\u2726\u2726"}</span>
                Funding
              </h4>
              <div className="acov-locked">
                <div className="acov-locked__preview acov-locked__preview--funding">
                  <div style={{filter: "blur(4px)", pointerEvents: "none", userSelect: "none"}}>
                    <div className="acov-funding__summary">
                      <div className="acov-stat-card"><span className="acov-stat-value">$65M</span><span className="acov-stat-label">Total Raised</span></div>
                      <div className="acov-stat-card"><span className="acov-stat-value">7</span><span className="acov-stat-label">Rounds</span></div>
                      <div className="acov-stat-card"><span className="acov-stat-value">22</span><span className="acov-stat-label">Investors</span></div>
                    </div>
                    <div className="acov-funding__ipo" style={{marginTop: 8}}>
                      <span className="acov-funding__ipo-label">IPO</span>
                      <span>Jun 23, 2004</span>
                      <span>{"\u00B7"} Valuation $1.1B</span>
                    </div>
                    <div className="acov-details" style={{marginTop: 8}}>
                      <div className="acov-details__row"><span className="acov-details__label">Last Round</span><span className="acov-details__value">Series D</span></div>
                      <div className="acov-details__row"><span className="acov-details__label">Lead Investor</span><span className="acov-details__value">Sequoia Capital</span></div>
                    </div>
                  </div>
                </div>
                <div className="acov-locked__cta">
                  <span className="acov-locked__icon">{"\uD83D\uDD12"}</span>
                  <span>Unlock funding data & investor details</span>
                  <button className="acov-locked__btn acov-locked__btn--premium" onClick={handlePremiumEnrichConfirm} disabled={premiumEnriching}>
                    {premiumEnriching ? "Enriching\u2026" : <><span>{"\u2726"}</span> Advanced Enrich <span className="acov-locked__credit">1 Credit</span></>}
                  </button>
                </div>
              </div>
            </section>
          )}

          {/* Funding — enriched data */}
          {(a?.lastFundingType || a?.fundingRounds > 0 || a?.ipoDate) && (
            <section className="acov-section acov-section--premium">
              <h4 className="acov-section__title">
                <span className="acov-stars" style={{color: "#7c3aed"}}>{"\u2726\u2726\u2726"}</span>
                Funding
                {val(a?.stockTicker) && <span className="acov-funding__ticker">{a.stockTicker}</span>}
              </h4>
              <div className="acov-funding">
                <div className="acov-funding__summary">
                  {a?.totalFundingAmount > 0 && (
                    <div className="acov-stat-card">
                      <span className="acov-stat-value">
                        ${a.totalFundingAmount >= 1000000000
                          ? (a.totalFundingAmount / 1000000000).toFixed(1) + "B"
                          : a.totalFundingAmount >= 1000000
                            ? (a.totalFundingAmount / 1000000).toFixed(0) + "M"
                            : (a.totalFundingAmount / 1000).toFixed(0) + "K"}
                      </span>
                      <span className="acov-stat-label">Total Raised</span>
                    </div>
                  )}
                  {a?.fundingRounds > 0 && (
                    <div className="acov-stat-card">
                      <span className="acov-stat-value">{a.fundingRounds}</span>
                      <span className="acov-stat-label">Rounds</span>
                    </div>
                  )}
                  {a?.numInvestors > 0 && (
                    <div className="acov-stat-card">
                      <span className="acov-stat-value">{a.numInvestors}</span>
                      <span className="acov-stat-label">Investors</span>
                    </div>
                  )}
                </div>
                {/* IPO info */}
                {val(a?.ipoDate) && (
                  <div className="acov-funding__ipo">
                    <span className="acov-funding__ipo-label">IPO</span>
                    <span>{fmtDate(a.ipoDate)}</span>
                    {a?.ipoValuation > 0 && <span>{"\u00B7"} Valuation ${(a.ipoValuation / 1000000000).toFixed(1)}B</span>}
                    {a?.ipoSharePrice > 0 && <span>{"\u00B7"} ${a.ipoSharePrice}/share</span>}
                  </div>
                )}
                {/* Funding timeline */}
                {list(a?.fundingHistory) && (
                  <div className="acov-funding__timeline">
                    {a.fundingHistory.map((round, i) => (
                      <div key={i} className="acov-funding__round">
                        <div className="acov-funding__round-dot" />
                        <div className="acov-funding__round-content">
                          <span className="acov-funding__round-type">{(round.type || "").replace(/_/g, " ")}</span>
                          {round.amount > 0 && (
                            <span className="acov-funding__round-amount">
                              ${round.amount >= 1000000
                                ? (round.amount / 1000000).toFixed(1) + "M"
                                : (round.amount / 1000).toFixed(0) + "K"}
                            </span>
                          )}
                          <span className="acov-funding__round-date">{round.date ? fmtDate(round.date) : ""}</span>
                          {round.investors && round.investors.length > 0 && (
                            <span className="acov-funding__round-investors">{round.investors.join(", ")}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* -- Data freshness -------------------------------------- */}
          {val(a?.lastDataUpdated) && (
            <div className="acov-data-freshness">
              Data last updated: {a.lastDataUpdated}
            </div>
          )}
        </div>

        {/* -- RIGHT: Find & Engage ---------------------------------- */}
        <div className="acov-col acov-col--right">

          <section className="acov-section acov-section--indigo">
            <h4 className="acov-section__title">
              <span className="acov-stars">{"\u2726\u2726\u2726"}</span>Prospect Insights
              {val(a?.domain) &&
                <button className="acov-section__view-all" onClick={() => goToListBuilder()}>
                  View all {"\u2192"}
                </button>
              }
            </h4>
            {totalContacts > 0 ? (
              <>
                <p className="acov-pi__summary">
                  You have <strong>{totalContacts}</strong> contact{totalContacts !== 1 ? "s" : ""} at {companyName}
                </p>
                <div className="acov-pi-grid">
                  {SENIORITY_LEVELS.map(s => (
                    <button key={s.key} className="acov-pi-card"
                      onClick={() => goToListBuilder("seniority", s.filter)}
                      disabled={!val(a?.domain)}>
                      <span className="acov-pi-card__icon">{s.icon}</span>
                      <span className="acov-pi-card__count">{bySeniority[s.key] || 0}</span>
                      <span className="acov-pi-card__label">{s.label}</span>
                    </button>
                  ))}
                </div>
                {mostContacted && (
                  <div className="acov-pi__most-contacted">
                    <span className="acov-pi__mc-label">Most contacted:</span>
                    <span className="acov-pi__mc-name">
                      {mostContacted.name}{mostContacted.title ? ` \u00B7 ${mostContacted.title}` : ""}
                    </span>
                  </div>
                )}
                {totalContacts >= 5 && (
                  aiAnalysis ? (
                    <div className="acov-pi__ai">
                      <div className="acov-pi__ai-header">
                        <h5 className="acov-pi__ai-title">{"\u2726"} AI Analysis</h5>
                        <div className="acov-pi__ai-meta">
                          {aiDate && <span className="acov-pi__ai-date">{aiDate}</span>}
                          {aiSaved && <span className="acov-pi__ai-saved">{"\u2713"} Saved to Notes</span>}
                        </div>
                      </div>
                      <p className="acov-pi__ai-text">{stripMd(aiAnalysis)}</p>
                      <button className="acov-pi__ai-refresh" onClick={() => handleAiAnalysis(true)}
                        disabled={aiLoading}>
                        {aiLoading
                          ? <><span className="acov-spinner acov-spinner--dark" />Refreshing...</>
                          : <><i className="material-icons">refresh</i> Get new insights</>}
                      </button>
                    </div>
                  ) : (
                    <button className="acov-pi__ai-btn" onClick={() => handleAiAnalysis(false)}
                      disabled={aiLoading || !val(a?.domain)}>
                      {aiLoading
                        ? <><span className="acov-spinner acov-spinner--dark" />Analyzing...</>
                        : <><span>{"\u2726"}</span> Get AI Prospect Analysis</>}
                    </button>
                  )
                )}
                <button className="acov-pi__discover" onClick={() => goToListBuilder()} disabled={!val(a?.domain)}>
                  <span className="acov-pi__discover-icon">{"\uD83D\uDD0D"}</span>
                  {totalContacts < 5 ? "Find more contacts to unlock AI insights" : `Discover more decision makers at ${companyName}`}
                </button>
              </>
            ) : (
              <>
                <p className="acov-pi__summary">Find key contacts at {companyName} to start engaging.</p>
                <button className="acov-pi__discover" onClick={() => goToListBuilder()} disabled={!val(a?.domain)}>
                  <span className="acov-pi__discover-icon">{"\uD83D\uDD0D"}</span>
                  Find contacts at {companyName}
                </button>
              </>
            )}
          </section>

          <section className="acov-section acov-section--activity">
            <h4 className="acov-section__title">
              <span className="acov-stars">{"\u2726\u2726\u2726"}</span>Activity & Engagement
            </h4>
            <div className="acov-unified">
              <div className="acov-unified__group">
                <span className="acov-unified__group-label">
                  <i className="material-icons-outlined acov-unified__group-icon acov-unified__group-icon--email">email</i>
                  Email
                </span>
                <div className="acov-unified__stats">
                  <StatCell label="Sent" value={sentCount} />
                  <StatCell label="Opened" value={openCount} />
                  <StatCell label="Clicked" value={clickCount} />
                  <StatCell label="Replied" value={replyCount} accent />
                </div>
              </div>
              <div className="acov-unified__group">
                <span className="acov-unified__group-label">
                  <LinkedinIcon className="acov-unified__group-icon acov-unified__group-icon--linkedin" />
                  LinkedIn
                </span>
                <div className="acov-unified__stats">
                  <StatCell label="CR Sent" value={liCrSent} />
                  <StatCell label="Accepted" value={liCrAccepted} accent />
                </div>
              </div>
              <div className="acov-unified__group">
                <span className="acov-unified__group-label">
                  <i className="material-icons-outlined acov-unified__group-icon acov-unified__group-icon--calls">phone_in_talk</i>
                  Calls & Tasks
                </span>
                <div className="acov-unified__stats">
                  <StatCell label="Calls Made" value={callsMade} />
                  <StatCell label="Tasks Done" value={tasksCompleted} accent />
                  <StatCell label="Tasks Due" value={tasksInDue} warn={tasksInDue > 0} />
                </div>
              </div>
            </div>
          </section>

          {/* Replaces the legacy coming-soon tile. Surfaces cached AccountResearch
              data (populated as a side-effect of any prior contact-DR run at
              this company) — adds the DR-specific layer (leadership team,
              hiring signals, competitors, customers, glassdoor) on top of the
              already-rendered tech/funding/profile sections. Stage 2 will wire
              the "Get Deep Research" CTA inside it to a standalone account-
              research pipeline. */}
          <AccountIntelligenceSection
            accountId={selectedSub || accountId}
            accountDomain={val(a?.domain)}
            accountName={companyName}
          />
        </div>
      </div>
    </div>
  );
});

/* ── Sub-components ─────────────────────────────────────────────── */

// Stage descriptions
const STAGE_DESC = {
  "New": "Newly added account, not yet worked on",
  "Working": "Actively being prospected or engaged",
  "Meeting Booked": "Meeting scheduled with a contact",
  "Qualified": "Confirmed as a good-fit account",
  "Negotiating": "In active deal discussions",
  "Disqualified": "Does not meet qualification criteria",
  "Unqualified": "Not a fit or low priority",
  "Nurture": "Long-term relationship building",
  "Closed Won": "Deal closed successfully",
  "Closed Lost": "Opportunity did not convert",
};

const AccountStageChip = ({ stage, setStage, stages, accountId, reloadCbk }) => {
  const [open, setOpen] = useState(false);
  const [savingId, setSavingId] = useState(null); // track which stage is saving

  const handleSelect = async (stageId, stageName) => {
    if (stageName === stage || savingId) return;
    setSavingId(stageId);
    // Close panel immediately, show spinner on chip
    setOpen(false);
    try {
      const r = await makeApi({
        url: URLS.updateAccountStage,
        method: "POST",
        data: { stageId, accountId }
      });
      if (+r?.status === 200) {
        setStage(stageName);
        toasterState.setToastMsg("Account stage updated successfully", "success");
        reloadCbk();
      } else {
        toasterState.setToastMsg("Failed to update stage", "failure");
      }
    } catch (e) {
      toasterState.setToastMsg("Failed to update stage", "failure");
    }
    setSavingId(null);
  };

  const filteredStages = (stages || []).filter(
    s => !["opted out", "do not contact", "bounced"].includes(s.displayName?.toLowerCase())
  );

  return (
    <div className="acov-stage">
      <button className="acov-stage__chip" onClick={() => !savingId && setOpen(!open)} disabled={!!savingId}>
        {savingId
          ? <span className="acov-spinner acov-spinner--stage" />
          : <span className="acov-stage__dot" />}
        <span className="acov-stage__name">{savingId ? "Updating..." : (stage || "Set stage")}</span>
        {!savingId && <i className="material-icons acov-stage__arrow">{open ? "expand_less" : "expand_more"}</i>}
      </button>
      {open && !savingId && (
        <>
          <div className="acov-stage__panel">
            <div className="acov-stage__panel-header">Update Account Stage</div>
            <ul className="acov-stage__list">
              {filteredStages.map(s => (
                <li key={s.id}
                  className={`acov-stage__option${s.displayName === stage ? " acov-stage__option--active" : ""}`}
                  onClick={() => handleSelect(s.id, s.displayName)}>
                  <div className="acov-stage__option-main">
                    <span className="acov-stage__option-dot" />
                    <span className="acov-stage__option-name">{s.displayName}</span>
                    {s.displayName === stage && <i className="material-icons acov-stage__check">check</i>}
                  </div>
                  {STAGE_DESC[s.displayName] && (
                    <span className="acov-stage__option-desc">{STAGE_DESC[s.displayName]}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
          <div className="acov-stage__overlay" onClick={() => setOpen(false)} />
        </>
      )}
    </div>
  );
};

const StatCell = ({ label, value, accent, warn }) => (
  <div className="acov-unified__cell">
    <span className={`acov-unified__num${accent ? " acov-unified__num--accent" : ""}${warn ? " acov-unified__num--warn" : ""}`}>{value}</span>
    <span className="acov-unified__label">{label}</span>
  </div>
);

// Inline-editable row
const EditableRow = ({ label, value, fieldKey, onSave, type, options, multiField }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value || "");
  const [saving, setSaving]   = useState(false);
  // For location multi-field
  const [draftCity, setDraftCity]       = useState(multiField?.city || "");
  const [draftState, setDraftState]     = useState(multiField?.state || "");
  const [draftCountry, setDraftCountry] = useState(multiField?.country || "");

  const startEdit = () => {
    setDraft(value || "");
    if (multiField) {
      setDraftCity(multiField.city || "");
      setDraftState(multiField.state || "");
      setDraftCountry(multiField.country || "");
    }
    setEditing(true);
  };

  const cancel = () => setEditing(false);

  const save = async () => {
    setSaving(true);
    if (multiField) {
      // Save each location field that changed
      let ok = true;
      if (draftCity !== (multiField.city || "")) ok = await onSave("city", draftCity);
      if (ok && draftState !== (multiField.state || "")) ok = await onSave("state", draftState);
      if (ok && draftCountry !== (multiField.country || "")) ok = await onSave("country", draftCountry);
      // If nothing changed, just close
      if (draftCity === (multiField.city || "") && draftState === (multiField.state || "") && draftCountry === (multiField.country || "")) ok = true;
      if (ok) setEditing(false);
    } else {
      const ok = await onSave(fieldKey, draft);
      if (ok) setEditing(false);
    }
    setSaving(false);
  };

  if (editing) {
    if (multiField) {
      return (
        <div className="acov-details__row acov-details__row--editing">
          <span className="acov-details__label">{label}</span>
          <div className="acov-details__edit-multi">
            <input className="acov-details__input" value={draftCity}
              onChange={e => setDraftCity(e.target.value)} placeholder="City" />
            <input className="acov-details__input" value={draftState}
              onChange={e => setDraftState(e.target.value)} placeholder="State" />
            <input className="acov-details__input" value={draftCountry}
              onChange={e => setDraftCountry(e.target.value)} placeholder="Country" />
            <div className="acov-details__edit-actions">
              <button className="acov-details__save-btn" onClick={save} disabled={saving}>
                <i className="material-icons">check</i>
              </button>
              <button className="acov-details__cancel-btn" onClick={cancel}>
                <i className="material-icons">close</i>
              </button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="acov-details__row acov-details__row--editing">
        <span className="acov-details__label">{label}</span>
        <div className="acov-details__edit-wrap">
          {type === "select" ? (
            <select className="acov-details__select" value={draft}
              onChange={e => setDraft(e.target.value)}>
              <option value="">Select</option>
              {options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input className="acov-details__input" value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => e.key === "Enter" && save()}
              autoFocus />
          )}
          <button className="acov-details__save-btn" onClick={save} disabled={saving}>
            <i className="material-icons">check</i>
          </button>
          <button className="acov-details__cancel-btn" onClick={cancel}>
            <i className="material-icons">close</i>
          </button>
        </div>
      </div>
    );
  }

  const isLink = value && ["linkedinId", "twitter"].includes(fieldKey);
  return (
    <div className="acov-details__row acov-details__row--editable">
      <span className="acov-details__label">{label}</span>
      {isLink ? (
        <a href={value.startsWith("http") ? value : `https://${value}`}
           target="_blank" rel="noopener noreferrer"
           className="acov-details__value acov-details__value--link">
          <span>{value}</span>
          <i className="material-icons-outlined acov-details__link-icon">open_in_new</i>
        </a>
      ) : (
        <span className={`acov-details__value${!value ? " acov-details__value--empty" : ""}`}>
          {value || E}
        </span>
      )}
      <button className="acov-details__edit-icon" onClick={startEdit} title={`Edit ${label}`}>
        <i className="material-icons">edit</i>
      </button>
    </div>
  );
};

const PhoneRow = ({ value, onCall, fieldKey, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(value || "");
  const [saving, setSaving]   = useState(false);

  const startEdit = () => { setDraft(value || ""); setEditing(true); };
  const cancel = () => setEditing(false);
  const save = async () => {
    setSaving(true);
    const ok = await onSave(fieldKey, draft);
    if (ok) setEditing(false);
    setSaving(false);
  };

  if (editing) {
    return (
      <div className="acov-details__row acov-details__row--editing">
        <span className="acov-details__label">Phone</span>
        <div className="acov-details__edit-wrap">
          <input className="acov-details__input" value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => e.key === "Enter" && save()}
            autoFocus />
          <button className="acov-details__save-btn" onClick={save} disabled={saving}>
            <i className="material-icons">check</i>
          </button>
          <button className="acov-details__cancel-btn" onClick={cancel}>
            <i className="material-icons">close</i>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="acov-details__row acov-details__row--editable">
      <span className="acov-details__label">Phone</span>
      {value
        ? <span className="acov-details__value acov-details__phone">
            {value}
            <button className="acov-details__call-btn" onClick={() => onCall(value)} title="Call this number">
              <i className="material-icons">call</i>
            </button>
          </span>
        : <span className="acov-details__value acov-details__value--empty">{E}</span>
      }
      <button className="acov-details__edit-icon" onClick={startEdit} title="Edit Phone">
        <i className="material-icons">edit</i>
      </button>
    </div>
  );
};

const Row = ({ label, value, link }) => (
  <div className="acov-details__row">
    <span className="acov-details__label">{label}</span>
    {value
      ? link
        ? <a href={value.startsWith("http") ? value : `https://${value}`}
             target="_blank" rel="noopener noreferrer"
             className="acov-details__value acov-details__value--link">
            <span>{value}</span>
            <i className="material-icons-outlined acov-details__link-icon">open_in_new</i>
          </a>
        : <span className="acov-details__value">{value}</span>
      : <span className="acov-details__value acov-details__value--empty">{E}</span>
    }
  </div>
);

const Skeleton = () => (
  <div className="acov acov--loading">
    <div className="acov-header">
      <div className="acov-header__left">
        <div className="linear-background acov-sk acov-sk--logo" />
        <div>
          <div className="linear-background acov-sk acov-sk--name" />
          <div className="linear-background acov-sk acov-sk--domain" />
        </div>
      </div>
      <div className="linear-background acov-sk acov-sk--btn" />
    </div>
    <div className="acov-body">
      <div className="acov-col acov-col--left">
        {[100, 60, 80, 70, 90].map((w, i) => (
          <div key={i} className="linear-background acov-sk"
            style={{ width: `${w}%`, height: 14, marginBottom: 10 }} />
        ))}
      </div>
      <div className="acov-col acov-col--right">
        {[80, 60, 70, 50, 90].map((w, i) => (
          <div key={i} className="linear-background acov-sk"
            style={{ width: `${w}%`, height: 14, marginBottom: 10 }} />
        ))}
      </div>
    </div>
  </div>
);

export default Main;
export { Main };
